import { isReportEnvironment } from '@/utils/environment.js'

// Per user preferences kept in the browser across sessions, such as filters and sorting of lists.
// Only used in UK/GB02, other environments keep nothing and work as before.
const prefix = 'nso.pref.'
const pageSizes = [10, 20, 50, 100]
const statuses = [0, 1, 2, 3, 4, 5]

function userKey (name) {
  let username = ''
  try {
    username = (localStorage.getItem('username') || '').toLowerCase()
  } catch (e) {
    // Storage blocked, preferences are then simply not kept
  }
  return `${prefix}${name}.${username}`
}

export function loadPreference (name) {
  if (!isReportEnvironment()) {
    return null
  }
  try {
    return localStorage.getItem(userKey(name))
  } catch (e) {
    return null
  }
}

export function savePreference (name, value) {
  if (!isReportEnvironment()) {
    return
  }
  try {
    if (value === null || value === undefined || value === '') {
      localStorage.removeItem(userKey(name))
    } else {
      localStorage.setItem(userKey(name), value)
    }
  } catch (e) {
    // Not kept, the page still works
  }
}

export function loadJsonPreference (name) {
  try {
    const value = loadPreference(name)
    const parsed = value ? JSON.parse(value) : null
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (e) {
    return null
  }
}

export function saveJsonPreference (name, value) {
  savePreference(name, value ? JSON.stringify(value) : null)
}

/**
 * Sorting, rows per page and status filter of a list, with anything no longer valid left out
 * (for example a column the list doesn't have any more). Null when nothing is saved.
 * @param {string} name preference name of the list
 * @param {string[]} sortableColumns columns the list can be sorted by
 */
export function loadListPreference (name, sortableColumns) {
  const saved = loadJsonPreference(name)
  if (!saved) {
    return null
  }
  const result = {}
  if (sortableColumns.includes(saved.sortColumn) && ['asc', 'desc'].includes(saved.sortOrder)) {
    result.sortColumn = saved.sortColumn
    result.sortOrder = saved.sortOrder
  }
  if (pageSizes.includes(saved.pageSize)) {
    result.pageSize = saved.pageSize
  }
  if (Array.isArray(saved.statuses) && saved.statuses.every(status => statuses.includes(status))) {
    result.statuses = saved.statuses
  }
  return result
}

export function saveListPreference (name, { sortColumn, sortOrder, pageSize, statuses }) {
  saveJsonPreference(name, { sortColumn, sortOrder, pageSize, statuses })
}

// Ends the session like before (everything in localStorage is removed, tokens included) but keeps preferences
export function clearSessionStorage () {
  try {
    const kept = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        kept.push([key, localStorage.getItem(key)])
      }
    }
    localStorage.clear()
    kept.forEach(([key, value]) => localStorage.setItem(key, value))
  } catch (e) {
    localStorage.clear()
  }
}
